// Mapbox helpers — token, geocoding (forward + reverse), small geo math.
import mapboxgl from "mapbox-gl";
// Bundle the version-matched Mapbox stylesheet with the JS so the canvas
// container has its required dimensions on first paint. The previous CDN
// @import in index.css loaded async and version-mismatched, which produced
// a 0×0 canvas (controls visible, tiles blank) on Vercel production.
import "mapbox-gl/dist/mapbox-gl.css";

export const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.VITE_MAPBOX_TOKEN ||
  "";

if (MAPBOX_TOKEN) mapboxgl.accessToken = MAPBOX_TOKEN;

export const MAP_STYLE = "mapbox://styles/mapbox/light-v11";
export const NEON  = "#D7FF2F";
export const INK   = "#0A0A0A";
export const CREAM = "#F4F0E1";

// ---------- Geocoding ----------
const GEO_BASE = "https://api.mapbox.com/geocoding/v5/mapbox.places";

export async function reverseGeocode(lng, lat) {
  if (!MAPBOX_TOKEN) return null;
  const url =
    `${GEO_BASE}/${lng},${lat}.json?` +
    `access_token=${MAPBOX_TOKEN}&language=en&limit=1&types=address,poi,neighborhood,locality,place`;
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const d = await r.json();
    const f = d.features?.[0];
    if (!f) return null;
    const ctx = f.context || [];
    const find = (k) => ctx.find((c) => c.id?.startsWith(k))?.text || null;
    return {
      exact: f.place_name || null,
      area:
        find("neighborhood") ||
        find("locality") ||
        find("place") ||
        find("district") ||
        f.text ||
        null,
      city: find("place") || find("locality") || null,
    };
  } catch {
    return null;
  }
}

export async function forwardGeocode(query, opts = {}) {
  if (!MAPBOX_TOKEN || !query || query.trim().length < 2) return [];
  const { proximity, limit = 6 } = opts;
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN,
    language: "en",
    limit: String(limit),
    types: "address,poi,neighborhood,locality,place,district",
  });
  if (proximity && Array.isArray(proximity)) {
    params.set("proximity", `${proximity[0]},${proximity[1]}`);
  }
  const url = `${GEO_BASE}/${encodeURIComponent(query)}.json?${params}`;
  try {
    const r = await fetch(url);
    if (!r.ok) return [];
    const d = await r.json();
    return (d.features || []).map((f) => ({
      label: f.place_name,
      sub: f.properties?.address || f.text,
      lng: f.center?.[0],
      lat: f.center?.[1],
    }));
  } catch {
    return [];
  }
}

// ---------- Geometry helpers ----------
// GeoJSON Polygon approximating a circle on the surface, in meters.
// Lightweight, no turf.js dep — good enough for the dimmed privacy radius.
export function circlePolygon(lng, lat, radiusM, steps = 64) {
  const earth = 6378137;
  const lat0 = (lat * Math.PI) / 180;
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * Math.PI * 2;
    const dx = (radiusM * Math.cos(t)) / (earth * Math.cos(lat0));
    const dy = (radiusM * Math.sin(t)) / earth;
    coords.push([
      lng + (dx * 180) / Math.PI,
      lat + (dy * 180) / Math.PI,
    ]);
  }
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [coords] },
    properties: {},
  };
}

// Expose mapboxgl for direct access (we keep one import for the consumer).
export { mapboxgl };
